<i18n
  src="@/locales/views/agro_exporter/finances/query_container_outcomes/show_org_invoice.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/container.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/org_invoice.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/org_invoice_item.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/org_invoice_payment.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/org_invoice_discount.yml" lang="yaml"></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('show_container')"
    :params="{
      agro_exporter_id: agro_exporter_id,
      agro_exporter_process_id: agro_exporter_process_id,
      container_id: container_id || org_invoice.container_id
    }"
  >
  </go-back-link>

  <bread-crumbs :labels="[module_name, agro_exporter_process.name]" />

  <template v-if="load_data_general">
    <div class="mt-8">
      <div class="panel">
        <div class="panel-header">
          <h3 class="panel-header-title">
            {{ $t("labels.title") }}
          </h3>
        </div>
        <div class="panel-body">
          <div class="space-y-8 divide-y divide-gray-200">
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.org_invoice.container") }}
                </dt>
                <dd class="content-data">
                  {{ org_invoice.container.container_code }}
                  <yes-no-label
                    :value="org_invoice.container.org_invoices_paid"
                    yes="Pagado"
                    no="No pagado"
                  />
                </dd>
              </div>
              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.container.customer") }}
                </dt>
                <dd class="content-data">
                  {{ org_invoice.container.customer.name }}
                </dd>
              </div>

              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.org_invoice.invoice_code") }}
                </dt>
                <dd class="content-data">
                  {{ org_invoice.invoice_code }}
                  <yes-no-label
                    :value="org_invoice.paid"
                    yes="Pagada"
                    no="No pagada"
                  />
                </dd>
              </div>
              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.org_invoice.invoice_date") }}
                </dt>
                <dd class="content-data">
                  {{ dateFormatter(org_invoice.invoice_date) }}
                </dd>
              </div>
              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.org_invoice.receipt_date") }}
                </dt>
                <dd class="content-data">
                  {{ dateFormatter(org_invoice.receipt_date) }}
                </dd>
              </div>
              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.org_invoice.amount") }}
                </dt>
                <dd class="content-data">
                  {{
                    currencyMoney(
                      org_invoice.amount,
                      org_invoice.currency.symbol
                    )
                  }}
                </dd>
              </div>
              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.org_invoice.scheduled_payment_at") }}
                </dt>
                <dd class="content-data">
                  {{ dateFormatter(org_invoice.scheduled_payment_at) }}
                </dd>
              </div>
              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.org_invoice.amount_paid") }}
                </dt>
                <dd class="content-data">
                  {{
                    currencyMoney(
                      org_invoice.amount_paid,
                      org_invoice.currency.symbol
                    )
                  }}
                </dd>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-8 flex flex-row">
      <h1 class="page-title">
        <i class="fa-solid fa-rectangle-list"></i>
        <span class="space-icon"></span>
        {{ $t("labels.title_org_invoice_items") }}
      </h1>
    </div>
    <div class="mt-8 flex flex-col">
      <div class="table-first-container">
        <div class="table-second-container">
          <div class="table-third-container">
            <table class="table">
              <thead class="table-header">
                <tr>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_item.quantity") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_item.description") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_item.unit_price") }}
                  </th>
                </tr>
              </thead>
              <tbody class="table-body">
                <tr
                  class="table-row"
                  v-for="(invoice_item, idx) in org_invoice.invoice_items"
                  :key="idx"
                >
                  <td class="table-cell">
                    {{ invoice_item.quantity }}
                  </td>
                  <td class="table-cell">
                    {{ invoice_item.description }}
                  </td>
                  <td class="table-cell">
                    {{
                      currencyMoney(
                        invoice_item.unit_price,
                        org_invoice.currency.symbol
                      )
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-8 flex flex-row">
      <h1 class="page-title">
        <i class="fa-solid fa-money-check-dollar"></i>
        <span class="space-icon"></span>
        {{ $t("labels.title_org_invoice_payments") }}
      </h1>
    </div>
    <div class="mt-4 flex flex-row">
      <div class="panel-plain-label">
        <badge-default
          :label="
            org_invoice_payments.length +
            ' ' +
            $t('labels.total_number_org_invoice_payments')
          "
        />
        <span class="space-actions"></span>
        <badge-default
          :label="
            org_invoice.currency.symbol +
            numberWithPrecision(total_amount_paid) +
            ' ' +
            org_invoice.currency.plural_name
          "
        />
      </div>
    </div>
    <div class="mt-4 flex flex-col">
      <div class="table-first-container">
        <div class="table-second-container">
          <div class="table-third-container">
            <table class="table">
              <thead class="table-header">
                <tr>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_payment.payment_at") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_payment.amount") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_payment.interbank_commission") }}
                  </th>
                </tr>
              </thead>
              <tbody class="table-body">
                <tr
                  class="table-row"
                  v-for="org_invoice_payment in org_invoice_payments"
                  :key="org_invoice_payment.id"
                >
                  <td class="table-cell">
                    {{ dateFormatter(org_invoice_payment.payment_at) }}
                  </td>
                  <td class="table-cell">
                    {{
                      currencyMoney(
                        org_invoice_payment.amount,
                        org_invoice.currency.symbol
                      )
                    }}
                  </td>
                  <td class="table-cell">
                    {{
                      currencyMoney(
                        org_invoice_payment.interbank_commission,
                        org_invoice.currency.symbol
                      )
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-8 flex flex-row">
      <h1 class="page-title">
        <i class="fa-solid fa-money-check-dollar"></i>
        <span class="space-icon"></span>
        {{ $t("labels.title_org_invoice_discounts") }}
      </h1>
    </div>
    <div class="mt-4 flex flex-row justify-between">
      <div class="panel-plain-label">
        <badge-default
          :label="
            org_invoice_discounts.length +
            ' ' +
            $t('labels.total_number_org_invoice_discounts')
          "
        />
      </div>
    </div>
    <div class="mt-4 flex flex-col">
      <div class="table-first-container">
        <div class="table-second-container">
          <div class="table-third-container">
            <table class="table">
              <thead class="table-header">
                <tr>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_discount.amount") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_discount.description") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_discount.exchange_rate") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_discount.discount_at") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_discount.discount_percent") }}
                  </th>
                </tr>
              </thead>
              <tbody class="table-body">
                <tr
                  class="table-row"
                  v-for="org_invoice_discount in org_invoice_discounts"
                  :key="org_invoice_discount.id"
                >
                  <td class="table-cell">
                    {{
                      currencyMoney(
                        org_invoice_discount.amount,
                        org_invoice_discount.currency.symbol
                      )
                    }}
                  </td>
                  <td class="table-cell">
                    {{ org_invoice_discount.description }}
                  </td>
                  <td class="table-cell">
                    {{ org_invoice_discount.exchange_rate }}
                  </td>
                  <td class="table-cell">
                    {{ dateFormatter(org_invoice_discount.discount_at) }}
                  </td>
                  <td class="table-cell">
                    {{ org_invoice_discount.discount_percent }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </template>
  <loading-message v-else />
</template>

<script>
import Requester from "@/views/agro_exporter/requesters/finances/query_container_outcomes/show_org_invoice.js";

export default {
  name: "AeFinancesContainerOutcomesShowOrgInvoice",
  props: {
    agro_exporter_process_id: { required: true },
    agro_exporter_id: { required: true },
    org_invoice_id: { required: true },
    container_id: {}
  },
  data() {
    return {
      load_data_general: false,
      agro_exporter_process: {},
      org_invoice: {},
      org_invoice_payments: [],
      org_invoice_discounts: []
    };
  },
  computed: {
    total_amount_paid: function () {
      return this.org_invoice_payments.reduce(
        (sum, payment) => sum + payment.amount,
        0
      );
    }
  },
  methods: {
    loadData: async function () {
      await this.fetchAgroExporterProcess();
      await this.fetchOrgInvoice();
      await this.fetchOrgInvoicePayments();
      await this.fetchOrgInvoiceDiscounts();
      this.load_data_general = true;
    },
    fetchAgroExporterProcess: async function () {
      let response = await Requester.fetchAgroExporterProcess(
        this.agro_exporter_id,
        this.agro_exporter_process_id
      );
      this.agro_exporter_process = response.data;
    },
    fetchOrgInvoice: async function () {
      let response = await Requester.fetchOrgInvoice(
        this.agro_exporter_id,
        this.org_invoice_id
      );
      this.org_invoice = response.data;
    },
    fetchOrgInvoicePayments: async function () {
      let response = await Requester.fetchOrgInvoicePayments(
        this.agro_exporter_id,
        this.org_invoice_id
      );
      this.org_invoice_payments = response.data;
      this.org_invoice_payments.forEach(org_invoice_payment => {
        org_invoice_payment["proc_button_destroy"] = false;
      });
    },
    fetchOrgInvoiceDiscounts: async function () {
      let response = await Requester.fetchOrgInvoiceDiscounts(
        this.agro_exporter_id,
        this.org_invoice_id
      );
      this.org_invoice_discounts = response.data;
      this.org_invoice_discounts.forEach(org_invoice_discount => {
        org_invoice_discount["proc_button_destroy"] = false;
      });
    }
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          agro_exporter_id: this.agro_exporter_id
        }
      }).then(() => this.loadData())
    );
  }
};
</script>
